import React from "react";
import PropTypes from "prop-types";
import { useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, IconButton, Paper, Tooltip } from "@mui/material";
import { UploadFile, CheckCircle, Cancel } from "@mui/icons-material";
import { DmsSpinner, fetchUrl, getToken, HTTP_STATUS } from "dmsbasereact";
import {intl} from "dmsbasereact";

export const DmsUploadOnly = forwardRef(({
    upload_url,
    onUploadSuccess,
    onUploadError,
    autoUpload = true,
    error,
    accept
}, ref) => {
    const token = getToken();
    const [showSpinner, setShowSpinner] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    const uploadFiles = useCallback(async (files) => {
        if (!files || files.length === 0) return;
        
        setShowSpinner(true);
        try {
            const uploadResults = [];
            
            for (const file of files) {
                const res = await fetchUrl(
                    upload_url,
                    "POST",
                    token,
                    file,
                    false,
                    true,
                    null,
                    null,
                    "multipart/form-data"
                );

                if (res.status === HTTP_STATUS.CREATED_201) {
                    uploadResults.push(res.data.data);
                    if (onUploadSuccess) {
                        onUploadSuccess(res.data.data);
                    }
                } else {
                    if (onUploadError) onUploadError(res);
                }
            }
            
            setSelectedFiles([]);
            return uploadResults;
            
        } catch (error) {
            if (onUploadError) onUploadError(error);
            throw error;
        } finally {
            setShowSpinner(false);
        }
    }, [upload_url, token, onUploadSuccess, onUploadError]);

    useImperativeHandle(ref, () => ({
        submitUpload: async () => {
            if (selectedFiles.length > 0) {
                return await uploadFiles(selectedFiles);
            }
            return [];
        },
        hasFiles: () => selectedFiles.length > 0,
        getSelectedFiles: () => selectedFiles,
        clearFiles: () => setSelectedFiles([])
    }));

    const onDrop = useCallback(async acceptedFiles => {
        if (autoUpload) {
            await uploadFiles(acceptedFiles);
        } else {
            setSelectedFiles(acceptedFiles);
        }
    }, [autoUpload, uploadFiles]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        accept,
    });

    const handleRemove = (e, index) => {
        e.stopPropagation();
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderStyle: 'dashed',
                borderColor: error ? 'error.main' : (isDragActive ? 'primary.main' : 'grey.400'),
                backgroundColor: isDragActive ? 'action.hover' : 'transparent',
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
            }}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            
            {showSpinner ? (
                <DmsSpinner />
            ) : selectedFiles.length > 0 ? (
                <Box width="100%">
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, textAlign: 'left' }}>
                        {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected
                    </Typography>
                    
                    <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                        {selectedFiles.map((file, index) => (
                            <Box 
                                key={index}
                                display="flex" 
                                alignItems="center" 
                                justifyContent="space-between" 
                                width="100%"
                                sx={{ py: 0.5, borderBottom: index < selectedFiles.length - 1 ? '1px solid #eee' : 'none' }}
                            >
                                <Box display="flex" alignItems="center" overflow="hidden">
                                    <CheckCircle color="success" sx={{ mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" noWrap title={file.name}>
                                        {file.name} ({Math.round(file.size / 1024)} KB)
                                    </Typography>
                                </Box>
                                <Tooltip title={intl.get("Tooltip.Action.Remove").d("Remove")}>
                                    <IconButton size="small" onClick={(e) => handleRemove(e, index)} sx={{ flexShrink: 0 }}>
                                        <Cancel />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                <Box display="flex" flexDirection="column" alignItems="center">
                    <UploadFile color="action" sx={{ mb: 1, fontSize: 48 }} />
                    <Typography variant="body2" color="textSecondary">
                        {intl.get("drag_drop_files").d("Drag n' drop files here, or click to select files")}
                    </Typography>
                </Box>
            )}
            
            {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {intl.get('RequiredField').d('This field is required')}
                </Typography>
            )}
        </Paper>
    );
});

DmsUploadOnly.propTypes = {
    upload_url: PropTypes.string,
    onUploadSuccess: PropTypes.func,
    onUploadError: PropTypes.func,
    autoUpload: PropTypes.bool,
    error: PropTypes.bool,
    accept: PropTypes.object
};
